import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { VideoIcon, SparklesIcon, ShareIcon, RefreshCwIcon, MaximizeIcon } from './icons'; // Using VideoIcon as CameraIcon

const CameraIconComp = VideoIcon;

interface TryOnMirrorProps {
    onClose?: () => void;
}

export const TryOnMirror: React.FC<TryOnMirrorProps> = ({ onClose }) => {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                setIsCameraActive(true);
            }
        } catch (err) {
            console.error("Camera access denied:", err);
            alert(t('tryon_camera_error') || "Could not access camera.");
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject as MediaStream;
            stream.getTracks().forEach(track => track.stop());
            videoRef.current.srcObject = null;
            setIsCameraActive(false);
        }
    };

    const takeSnapshot = () => {
        if (videoRef.current) {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(videoRef.current, 0, 0);
            const dataUrl = canvas.toDataURL('image/png');
            setCapturedImage(dataUrl);
            stopCamera();

            // Mock AI processing
            setIsProcessing(true);
            setTimeout(() => setIsProcessing(false), 2000);
        }
    };

    const resetTryOn = () => {
        setCapturedImage(null);
        startCamera();
    };

    useEffect(() => {
        startCamera();
        return () => stopCamera();
    }, []);

    return (
        <div className="relative w-full h-full bg-black rounded-3xl overflow-hidden shadow-2xl border-4 border-gray-800">
            {/* Main Viewport */}
            <div className="absolute inset-0 flex items-center justify-center">
                {!capturedImage ? (
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover transform scale-x-[-1]" // Mirror effect
                    />
                ) : (
                    <img src={capturedImage} alt="Try-On Result" className="w-full h-full object-cover" />
                )}
            </div>

            {/* UI Overlays */}
            <div className="absolute inset-0 bg-gradient-to-input pointer-events-none">
                {/* Header */}
                <div className="absolute top-0 left-0 w-full p-6 flex justify-between items-center bg-gradient-to-b from-black/60 to-transparent">
                    <h2 className="text-white font-serif text-xl tracking-widest uppercase flex items-center gap-2">
                        <SparklesIcon className="w-5 h-5 text-indigo-400" />
                        {t('tryon_title') || "AI Mirror"}
                    </h2>
                    {onClose && (
                        <button onClick={onClose} className="pointer-events-auto text-white/80 hover:text-white" title={t('common_close') || "Close"}>
                            <MaximizeIcon className="w-6 h-6 rotate-45" /> {/* Close X mockup */}
                        </button>
                    )}
                </div>

                {/* AI Processing Overlay */}
                <AnimatePresence>
                    {isProcessing && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center z-10"
                        >
                            <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4" />
                            <p className="text-white font-medium tracking-wide animate-pulse">
                                {t('tryon_processing') || "Analyzing fit & vibe..."}
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Analysis/Vibe Check Result */}
                {!isProcessing && capturedImage && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute top-24 right-6 w-64 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-white pointer-events-auto"
                    >
                        <h3 className="font-bold mb-1 text-indigo-300">Vibe Check</h3>
                        <p className="text-sm opacity-90">"Effortlessly chic! The fit is perfect for your body type. Try adding a silver necklace to pop the neckline."</p>
                        <div className="mt-3 flex gap-2">
                            <span className="px-2 py-1 bg-white/20 rounded text-xs">#Chic</span>
                            <span className="px-2 py-1 bg-white/20 rounded text-xs">#Evening</span>
                        </div>
                    </motion.div>
                )}

                {/* Controls */}
                <div className="absolute bottom-0 w-full p-8 flex justify-center items-center gap-8 bg-gradient-to-t from-black/80 to-transparent pointer-events-auto">
                    {!capturedImage ? (
                        <button
                            onClick={takeSnapshot}
                            className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center hover:scale-105 transition-transform bg-white/10 backdrop-blur"
                            title={t('tryon_capture') || "Take Photo"}
                        >
                            <div className="w-16 h-16 bg-white rounded-full" />
                        </button>
                    ) : (
                        <>
                            <button onClick={resetTryOn} className="p-4 rounded-full bg-gray-800/80 text-white hover:bg-gray-700 backdrop-blur" title={t('tryon_retake') || "Retake"}>
                                <RefreshCwIcon className="w-6 h-6" />
                            </button>
                            <button className="p-4 rounded-full bg-indigo-600/90 text-white hover:bg-indigo-500 shadow-lg shadow-indigo-500/30 backdrop-blur" title={t('tryon_share') || "Share"}>
                                <ShareIcon className="w-6 h-6" />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
